<script setup lang="ts">
import { computed } from "vue";

import type { ColumnsStack, ChangeValue } from "../sbi/protocol";
import type { TreeNode } from "../sbi/validate-render-op";
import { compactSpans, MAX_TRACKS } from "./columns-layout";
import InsightNode from "./InsightNode.vue";

// Renders a `columns` tree node as a CSS grid. The `total` is the sum
// of cell spans — fed straight into `grid-template-columns` — and each
// cell wraps its children in a `<div>` whose `grid-column` claims its
// own span.
//
// Responsive policy lives here, not with the author, and keys off the
// row's CONTAINER (the embed, or the cell / tab / section it sits in)
// via `@container`, never the viewport: full grid ≥ 640px, two tracks
// at 480–639px (spans re-packed by `compactSpans`), one per row below
// 480px. `stack` lets the author pick "compact" (one per row below
// 640px) or "never". In print the top-level row sits in a 680px slot,
// so it keeps the authored grid; rows nested in a narrow print cell
// still reflow, which is what fits on the page.
//
// `matchHeight` (the author's `sbi.columns(cells, { matchHeight: true })`
// flag) flips the row into "uniform tile" mode: cells stretch to the
// row's tallest cell (the grid default) AND each cell's leaves grow to
// fill the cell, so a row of KPIs with uneven decoration (caption,
// delta, spark on some tiles only) reads as a single uniform strip.
const props = withDefaults(
  defineProps<{
    total: number;
    cells: ReadonlyArray<{ span: number; children: TreeNode[] }>;
    loading?: boolean;
    matchHeight?: boolean;
    stack?: ColumnsStack;
    /** Per-pass field → format registry; forwarded to every child node. */
    formats?: Record<string, string>;
  }>(),
  {
    formats: () => ({}),
  },
);

defineEmits<{
  (e: "click-target", id: string, datum?: Record<string, unknown>, rowIndex?: number): void;
  (e: "change-target", id: string, value: ChangeValue): void;
  /** Forwarded from a descendant modal drilldown's X / Esc / backdrop. */
  (e: "chrome-click-target", id: string): void;
  /** Forwarded from descendant accordions so EmbedHost can surface the DOM event. */
  (e: "accordion-toggle", summary: string, open: boolean): void;
  /** Forwarded from descendant callouts so EmbedHost can surface the DOM event. */
  (e: "callout-rendered", kind: string, title?: string): void;
}>();

// The runtime already clamps spans; clamping again keeps a hand-built
// tree (tests, future emitters) from claiming more than 12 tracks.
const spans = computed(() =>
  props.cells.map((c) => Math.min(MAX_TRACKS, Math.max(1, c.span))),
);
// The track count is the row's own sum, NOT capped at 12: a row is one
// line by contract, so 16 bare KPIs are 16 tracks and `[6,6,6,6]` is four
// equal columns. Capping it would wrap the excess onto a second line.
const total = computed(() => spans.value.reduce((a, b) => a + b, 0) || props.total);
const compact = computed(() => compactSpans(spans.value, total.value));
</script>

<template>
  <div
    class="cols"
    :class="{
      'cols--match-height': matchHeight,
      'cols--stack-compact': stack === 'compact',
      'cols--stack-never': stack === 'never',
    }"
    :style="{ '--total': total, '--compact-tracks': Math.min(total, 2) }"
  >
    <div
      v-for="(cell, ci) in cells"
      :key="ci"
      class="cell"
      :style="{ '--span': spans[ci], '--span-compact': compact[ci] }"
    >
      <InsightNode
        v-for="(child, i) in cell.children"
        :key="i"
        :node="child"
        :loading="loading"
        :formats="formats"
        @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
        @change-target="(id, value) => $emit('change-target', id, value)"
        @chrome-click-target="(id) => $emit('chrome-click-target', id)"
        @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
        @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
      />
    </div>
  </div>
</template>

<style scoped>
.cols {
  display: grid;
  gap: var(--_lucent-cols-gap, 16px);
  grid-template-columns: repeat(var(--total, 4), minmax(0, 1fr));
  margin: var(--space-4) 0;
}
.cell {
  grid-column: span var(--span, 1);
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  /* Container slot: nested components' @container queries measure
     this cell, not the page. Safe because the slot is stretched by
     its parent, never sized to its content. */
  container-type: inline-size;
}
/* matchHeight: the grid already stretches each cell to the row's
   tallest sibling; we additionally let every leaf inside the cell
   flex to fill, so a KPI card grows to occupy the cell rather than
   sitting at the top with whitespace beneath it. `:deep` reaches
   through to the leaf component's root since component roots are
   scoped under the leaf, not the cell. */
.cols--match-height .cell > :deep(*) {
  flex: 1 1 auto;
  min-height: 0;
}

/* Compact (480–639px of container): two tracks, spans re-packed by
   `compactSpans`. matchHeight still applies — a two-up line is still
   a row. `stack: "compact"` skips straight to one per row. These
   query the nearest ancestor container — the slot this row sits in. */
@container (max-width: 639px) {
  .cols:not(.cols--stack-never) {
    --_lucent-cols-gap: 12px;
    grid-template-columns: repeat(var(--compact-tracks, 2), minmax(0, 1fr));
  }
  .cols:not(.cols--stack-never) > .cell {
    grid-column: span var(--span-compact, 1);
  }
  /* Doubled class: must outrank `.cols:not(.cols--stack-never)` above. */
  .cols.cols--stack-compact {
    grid-template-columns: minmax(0, 1fr);
  }
  .cols.cols--stack-compact > .cell {
    grid-column: 1 / -1;
  }
  .cols--stack-compact.cols--match-height .cell > :deep(*) {
    flex: 0 0 auto;
  }
}
/* Narrow (< 480px): one per row. matchHeight is meaningless once
   cells stack — drop the leaf stretch so one tall KPI doesn't drag
   the whole stack to its height. */
@container (max-width: 479px) {
  .cols:not(.cols--stack-never) {
    grid-template-columns: minmax(0, 1fr);
  }
  .cols:not(.cols--stack-never) > .cell {
    grid-column: 1 / -1;
  }
  .cols--match-height:not(.cols--stack-never) .cell > :deep(*) {
    flex: 0 0 auto;
  }
}
</style>
